import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp, submitOtp } from "../../Api/api";
import { FaSpinner } from "react-icons/fa";
import { showToast } from "../../utils";
import { getUserRoleAndStep } from "./PrivateRoute";
import { markDonor } from "../../Services/services/authapi";
import { useDispatch, useSelector } from "react-redux";
import { setUserRole, setUserStep } from "../../Redux/userSlice";

const SignUpForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });

  const [showOTPField, setShowOTPField] = useState(false);
  const [otp, setOTP] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    (async () => {
      const user = await getUserRoleAndStep(dispatch);
      if (user && user.role && user.step === 1) {
        setShowRoleSelection(true);
        dispatch(setUserRole(user.role));
        dispatch(setUserStep(1));
      } else if (user && user.step === 2) {
        if (user.role === "admin") {
          navigate("/onboarding/ngodocuments");
        } else if (user.role === "individual") {
          navigate("/onboarding/userdocuments");
        }
        dispatch(setUserStep({ step: 2 }));
      } else if (user && user.step === 3) {
        dispatch(setUserStep({ step: 3 }));
      }
    })();
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "password") {
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!password) {
      setPasswordError("Password is required.");
    } else if (!strongPasswordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character."
      );
    } else {
      setPasswordError(""); // Clear the error if valid
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordError) {
      showToast("Please fix the password issues before submitting.");
      return;
    }
    setIsLoading(true);
    try {
      await signUp(formData);
      setShowOTPField(true);
      showToast("Please enter the OTP sent to your email.");
    } catch (error) {
      showToast("Email already registered");
    }
    setIsLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await submitOtp({ email: formData.email, code: otp });
      if (response.success) {
        setShowOTPField(false);
        showToast("OTP verified successfully. Select your role to continue.");
        const userData = await getUserRoleAndStep();
        if (userData) {
          dispatch(setUserRole(userData.role));
          dispatch(setUserStep(userData.step));
        }
        setShowRoleSelection(true);
      } else {
        showToast("Error verifying OTP");
      }
    } catch (error) {
      showToast("Error verifying OTP");
    }
    setIsLoading(false);
  };

  const handleRoleSelect = async (role) => {
    dispatch(setUserRole(role));
    setSelectedRole(role);

    await navigateBasedOnRole(role);
  };

  const navigateBasedOnRole = async (role) => {
    switch (role) {
      case "donor":
        await markDonor(dispatch);
        navigate("/");
        break;
      case "individual":
        navigate("/onboarding/userform");
        break;
      case "organization":
        navigate("/onboarding/ngoform");
        break;
      default:
        showToast("Please select a valid role");
    }
  };

  const handleTermsChange = (e) => {
    setIsTermsChecked(e.target.checked);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen max-h-[100vh]">
      <div className="h-64 md:h-full md:w-3/5 max-h-[100vh]">
        <img
          className="h-[100vh] w-full object-cover"
          src="https://d1irs7zslorh1g.cloudfront.net/assets/slider/hands-cupping-heart-love-modified.jpg"
          alt="signup-image"
        />
      </div>
      {!showRoleSelection && (
        <div className="flex flex-col justify-center p-4 md:p-12 w-full md:w-3/5 bg-white h-[screen] overflow-auto">
          <form
            onSubmit={
              showRoleSelection
                ? (e) => e.preventDefault()
                : showOTPField
                ? handleOTPSubmit
                : handleSubmit
            }
            className="max-w-md mx-12"
          >
            <div className="flex mt-4 gap-2 ">
              <p>Already a member?</p>
              <p
                onClick={() => navigate("/login")}
                className="text-btn-1 cursor-pointer underline"
              >
                Sign In
              </p>
            </div>
            <h1 className="text-2xl mb-3 mt-8">Register Your Account</h1>
            <div className="py-3">
              <label htmlFor="first_name" className="block">
                First Name:
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="border rounded-lg py-2 w-full px-2"
                required
              />
            </div>
            <div className="py-3">
              <label htmlFor="last_name" className="block">
                Last Name:
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="border rounded-lg py-2 w-full px-2"
                required
              />
            </div>
            <div className="py-3">
              <label htmlFor="email" className="block">
                Email:
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="border rounded-lg py-2 w-full px-2"
                required
                disabled={showOTPField}
              />
            </div>
            {!showOTPField && (
              <div className="py-3">
                <label htmlFor="password" className="block">
                  Password:
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="border rounded-lg py-2 w-full px-2"
                  required
                />
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                )}
                <div className="py-3 flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={isTermsChecked}
                    onChange={handleTermsChange}
                    className="mr-2"
                  />
                  <label htmlFor="terms">
                    I agree to the{" "}
                    <Link
                      to="/terms-and-conditions"
                      className="text-blue-500 hover:text-blue-700 underline"
                    >
                      Terms & Conditions
                    </Link>
                  </label>
                </div>
              </div>
            )}
            {showOTPField && (
              <div className="py-3">
                <label htmlFor="otp" className="block">
                  OTP:
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOTP(e.target.value)}
                  className="border rounded-lg py-2 w-full px-2"
                  required
                />
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="submit"
                className={`rounded-lg  text-white w-full py-2 text-md mt-4 ${isTermsChecked?" bg-btn-1":"bg-gray-500"} duration-200 ease-in-out `}
                disabled={isLoading || !isTermsChecked}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <FaSpinner className="animate-spin" />
                  </div>
                ) : showOTPField ? (
                  "Verify OTP"
                ) : (
                  "Register"
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      {showRoleSelection && (
        <div className="flex flex-col items-end justify-center p-10  mb-4 ">
          <h1 className="text-2xl mr-20">Who are You?</h1>
          {["donor", "individual", "organization"].map((role) => (
            <button
              key={role}
              type="button"
              className={`w-72 text-center py-2 bg-blue-500 hover:bg-blue-700 text-white rounded-xl transition duration-300 m-2 ease-in-out ${
                selectedRole === role ? "bg-blue-700" : ""
              }`}
              onClick={() => handleRoleSelect(role)}
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignUpForm;
